import { useState, useContext, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import * as Screens from "src/screens";
import { useUrlQuery } from "src/utils/Route";
import { useTranslation } from "react-i18next";
import styles from "./Service.module.css";
import { runInAction } from "mobx";
import {
    NotificationSubscriptionComponent,
    NotificationSubscriptionComponentProps
} from "src/network/NotificationSubscriptionComponent";
import { observer } from "mobx-react";

export interface InspectionInformation {
    customer: string;
    date: string;
    objectstreet: string;
    objectcity: string;
}

export const DIRECT_ROUTING_PREFIX: string = "99";

enum STATE {
    INIT = 0,
    LOADING = 1,
    SUCCESS = 2,
    ERROR = 3,
    ALREADY_SUBMITTED = 4
}

enum CONNECTION_TYPE {
    SERVICE7000 = 4,
    YAROWA = 5
}

enum RESPONSE_CODE {
    SUCCESS = 1,
    TRANSMITTED_WITH_THIRD_PARTY_ERROR = 2,
    ALREADY_TRANSMITTED = 3,
    MISSING_DATA = 5
}

const YAROWA_REDIRECTION_TIMEOUT = 3000;
const YAROWA_DASHBOARD_SUBFOLDER: string = "/dashboard";

const ServiceBase = () => {
    const { t } = useTranslation();
    const { easyContactStore, navStore, authStore } = useContext(RootStoreContext);

    const [state, setState] = useState<STATE>(STATE.INIT);
    const [notificationCompState, setNotificationCompState] = useState<NotificationSubscriptionComponentProps>({
        customerid: undefined,
        id: undefined
    });
    const [notificationForwardedContactPerson, setNotificationForwardedContactPerson] = useState<string>("");
    const [notificationForwardedUri, setNotificationForwardedUri] = useState<string>("");
    const [errorText, setErrorText] = useState<string>("");

    const urlQuery = useUrlQuery();
    const codeInRequest = urlQuery.get("code");
    const typeInRequest = urlQuery.get("type");
    const { serviceTypeOfRequest, isDirectlyForwardedRequest } = GetServiceTypeOfRequest();

    useEffect(() => {
        runInAction(() => {
            navStore.setTitle(
                t(
                    serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000
                        ? "screens.service.title_service_7000"
                        : serviceTypeOfRequest === CONNECTION_TYPE.YAROWA
                        ? "screens.service.title_yarowa"
                        : "screens.service.title_undefined_service"
                )
            );
            navStore.setBackbutton(null);
        });
    }, [navStore, t, serviceTypeOfRequest]);

    useEffect(() => {
        if (notificationCompState.customerid && notificationCompState.id) {
            setTimeout(() => {
                setNotificationCompState({ customerid: undefined, id: undefined });
                if (
                    !easyContactStore.notification.directsendready &&
                    !easyContactStore.notification.abortdirectsend // subscription already stopped, do not send the service order again
                ) {
                    easyContactStore.setDirectSendReady(true);
                }
            }, 70000); // After 70 secs: stop waiting for success and, if no subscription error occured, try forwarding anyway.
        }
    }, [notificationCompState, easyContactStore, authStore.user?.customerid]);

    function GetYarowaUrlFragments(data: any) {
        const yarowaDashboardUri =
            data?.uri?.length > 0
                ? data.uri + YAROWA_DASHBOARD_SUBFOLDER
                : NetworkConfig.yarowaFrontentDefaultUrl + YAROWA_DASHBOARD_SUBFOLDER;

        let urlFragments: string = data?.notificationid.length === 0 ? "" : "?ExternalSystemId=" + data.notificationid;
        if (urlFragments.length > 0) {
            if (
                data?.servicingaddress_zip !== undefined &&
                data?.servicingaddress_zip?.length > 0 &&
                data?.servicingaddress_city !== undefined &&
                data?.servicingaddress_city?.length > 0
            ) {
                urlFragments +=
                    "&address.zipCode=" +
                    data.servicingaddress_zip +
                    "&address.municipality=" +
                    data.servicingaddress_city;
            }

            urlFragments += "&action=CreateWO";
        }

        return yarowaDashboardUri + urlFragments;
    }

    const updateNotificationForwardedUri = useCallback((result: any) => {
        // Open frontend for manual input when receiving an error
        let notificationForwardUri = result?.data?.uri + YAROWA_DASHBOARD_SUBFOLDER;

        if (
            result?.data?.status === RESPONSE_CODE.SUCCESS ||
            result?.data?.status === RESPONSE_CODE.ALREADY_TRANSMITTED
        ) {
            notificationForwardUri = GetYarowaUrlFragments(result.data);
        }

        setNotificationForwardedUri(notificationForwardUri);
    }, []);

    const redirectToYarowa = useCallback(
        (state: STATE, url: string = "") =>
            () => {
                // When an url is passed as argument: Open this url.
                let urlToOpen = url;
                if (urlToOpen.length === 0) {
                    // Otherwise open the composed link standing in notificationForwardedUri if present, else open the dashboard
                    urlToOpen =
                        notificationForwardedUri.length > 0
                            ? notificationForwardedUri
                            : NetworkConfig.yarowaFrontentDefaultUrl + YAROWA_DASHBOARD_SUBFOLDER;
                }

                window.open(urlToOpen, "_self");
            },
        [notificationForwardedUri]
    );

    const handleForwardNotificationToYarowaSuccessful = useCallback(
        (data: any) => {
            if (data?.status !== undefined) {
                const notificationForwardUri = GetYarowaUrlFragments(data);

                setNotificationForwardedUri(notificationForwardUri);
                setTimeout(() => {
                    redirectToYarowa(STATE.SUCCESS, notificationForwardUri)();
                }, YAROWA_REDIRECTION_TIMEOUT);
            }
        },
        [redirectToYarowa]
    );

    const onSendServiceOrder = useCallback(async () => {
        setState(STATE.LOADING);
        try {
            const access_token =
                authStore.token === undefined // TODO: Braucht es diesen Check? authStore.token wird nur beim 'login' gesetzt. Bei dem Token handelt es sich aber nicht um einen ClientCredential Token
                    ? await authStore.authClient.getECClientCredentialToken()
                    : authStore.token;

            const forwardingType = serviceTypeOfRequest > 0 ? serviceTypeOfRequest.toString() : typeInRequest;
            const response = await fetch(
                `${NetworkConfig.postForwardNotificationUrl}?code=${codeInRequest}&type=${forwardingType}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );

            const result = await response.json();
            if (result?.data?.uri?.length > 0) {
                updateNotificationForwardedUri(result);
            }

            if (response.status === 200 && response.ok) {
                if (result.data.status === RESPONSE_CODE.SUCCESS) {
                    if (serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000) {
                        setNotificationForwardedContactPerson(result.data.contactperson);
                    } else if (serviceTypeOfRequest === CONNECTION_TYPE.YAROWA) {
                        handleForwardNotificationToYarowaSuccessful(result.data);
                    }
                    setState(STATE.SUCCESS);
                } else if (result.data.status === RESPONSE_CODE.TRANSMITTED_WITH_THIRD_PARTY_ERROR) {
                    setState(STATE.ERROR);
                } else if (result.data.status === RESPONSE_CODE.ALREADY_TRANSMITTED) {
                    setState(STATE.ALREADY_SUBMITTED);
                } else if (result.data.status === RESPONSE_CODE.MISSING_DATA) {
                    const connectionType =
                        serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000
                            ? "Service 7000"
                            : serviceTypeOfRequest === CONNECTION_TYPE.YAROWA
                            ? "Yarowa"
                            : "die Schnittstelle des Dienstleisters";
                    setErrorText(t("screens.service.error_text_missing_data", { connectionType }));
                    setState(STATE.ERROR);
                } else {
                    setErrorText(t("screens.service.error_text_generic"));
                    setState(STATE.ERROR);
                }
            } else if (response.status === 400) {
                console.error("Statuscode: 400 | Message" + result.message);
                setState(STATE.ERROR);
            } else {
                console.error("Statuscode: " + response.status + " | Message: " + result.message);
                console.error(`Error: ${result.error}, Code: ${result.code}`);
                setState(STATE.ERROR);
            }
        } catch (err) {
            console.error("Fetch error occured: ", err);
        }
    }, [
        codeInRequest,
        typeInRequest,
        serviceTypeOfRequest,
        handleForwardNotificationToYarowaSuccessful,
        updateNotificationForwardedUri,
        authStore.token,
        t,
        authStore.authClient
    ]);

    useEffect(() => {
        if (easyContactStore.notification.directsendready) {
            setNotificationCompState({ customerid: undefined, id: undefined });
            easyContactStore.setDirectSendReady(false);
            onSendServiceOrder();
        }
    }, [easyContactStore.notification.directsendready, easyContactStore, onSendServiceOrder]);

    useEffect(() => {
        if (easyContactStore.notification.abortdirectsend) {
            setNotificationCompState({ customerid: undefined, id: undefined });
            onSendServiceOrder();
        }
    }, [easyContactStore.notification.abortdirectsend, easyContactStore, onSendServiceOrder]);

    function GetServiceTypeOfRequest() {
        // Ermittelt den Forwarding Type z.B.   typeInRequest: 995?t=Qz0xZjA5MzMwMS02NWJmLTQ1ZDctOWZmZS1jNGNjMWY3Nzg2MTk=
        //                                      serviceTypeOfRequest: 5
        const isDirectlyForwardedRequest =
            typeInRequest?.startsWith(DIRECT_ROUTING_PREFIX) && typeInRequest?.length > DIRECT_ROUTING_PREFIX.length;
        const typeParamPart2 = isDirectlyForwardedRequest
            ? typeInRequest?.substring(DIRECT_ROUTING_PREFIX.length)
            : typeInRequest;
        const typeParamAsString: string = typeParamPart2 == null || typeParamPart2 === undefined ? "" : typeParamPart2;
        const serviceTypeOfRequest = Number(typeParamAsString.split("?")[0]);

        return { serviceTypeOfRequest, isDirectlyForwardedRequest };
    }

    const handleDirectSend = useCallback(async () => {
        setState(STATE.LOADING);

        const decoded: string = atob(codeInRequest ?? "");
        var splits = decoded.split("/");
        var splittingIsOk = splits.length === 2;
        if (!splittingIsOk) {
            console.error(`Error splitting code in 2 pieces. Code is: ${decoded}.`);
            easyContactStore.setAbortDirectSend(true);
        } else if (authStore.user?.customerid !== undefined && splits[0] !== authStore.user?.customerid) {
            const msg = `Subscribing to a different customer is not allowed. Customer in notification '${splits[0]}'`;
            console.error(`${msg} does not match customer in token: '${authStore.user?.customerid}'.`);
            easyContactStore.setAbortDirectSend(true);
        } else {
            setNotificationCompState({ customerid: splits[0], id: splits[1] });
        }
    }, [authStore.user?.customerid, codeInRequest, easyContactStore]);

    const onClosePage = useCallback(() => {
        window.close();
    }, []);

    if (!codeInRequest) {
        return <Screens.Error message="Code ist nicht vorhanden oder ist ungültig" />;
    }

    if (serviceTypeOfRequest !== CONNECTION_TYPE.SERVICE7000 && serviceTypeOfRequest !== CONNECTION_TYPE.YAROWA) {
        return <Screens.Error message="Type ist nicht vorhanden oder ist ungültig" />;
    }

    if (state === STATE.INIT && isDirectlyForwardedRequest) {
        handleDirectSend();
    }

    return (
        <UI.Container>
            <UI.Row className="justify-content-center mt-2">
                <UI.Col md={8}>
                    <div className={`${styles.ServiceContentContainer} text-center`}>
                        {state === STATE.INIT ? (
                            <>
                                <h2 className={styles.WelcomeTitle}>
                                    {t(
                                        serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000
                                            ? "screens.service.title_service_7000"
                                            : "screens.service.title_yarowa"
                                    )}
                                </h2>
                                <p className={styles.ReadyToSendText}>
                                    {t(
                                        serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000
                                            ? "screens.service.send_order_text_service_7000"
                                            : "screens.service.send_order_text_yarowa"
                                    )}
                                </p>
                                <UI.Button
                                    onClick={onSendServiceOrder}
                                    className={`${styles.ServiceButton} mx-auto my-4`}
                                    label={t("screens.service.send_order_button")}
                                />
                                <UI.Button
                                    onClick={onClosePage}
                                    className={`${styles.ServiceButton} mx-auto my-4`}
                                    label={t("screens.service.close_page_button")}
                                    variant="link"
                                />
                            </>
                        ) : undefined}

                        {state === STATE.LOADING ? (
                            <>
                                <img src={NetworkConfig.brandAssets.logoImage} alt="Logo" width={150} />
                                <p className={styles.LoadingText}>{t("screens.service.loading_text")}...</p>
                                <UI.RotatingSpinner noLogo />
                            </>
                        ) : undefined}

                        {state === STATE.SUCCESS && serviceTypeOfRequest === CONNECTION_TYPE.YAROWA ? (
                            <>
                                <img src={NetworkConfig.brandAssets.logoImage} alt="Logo" width={150} />
                                <p></p>
                                <UI.RotatingSpinner noLogo />
                                <p className={styles.SuccessText}>
                                    {t("screens.service.success_redirection_to_yarowa_text.paragraph1")}
                                </p>
                                <p className={styles.SuccessText}>
                                    {t("screens.service.success_redirection_to_yarowa_text.paragraph2")}
                                </p>
                                <UI.Button
                                    onClick={redirectToYarowa(state)}
                                    className={`${styles.ServiceButton} mx-auto my-4`}
                                    label={t("screens.service.redirect_to_yarowa_button")}
                                />
                                <UI.Button
                                    onClick={onClosePage}
                                    className={`${styles.ServiceButton} mx-auto my-4`}
                                    label={t("screens.service.close_page_button")}
                                    variant="link"
                                />
                            </>
                        ) : undefined}

                        {state === STATE.SUCCESS && serviceTypeOfRequest !== CONNECTION_TYPE.YAROWA ? (
                            <>
                                <UI.Icon icon={UI.SVGIcon.Check} size={"xl"} />
                                <h2 className={styles.SuccessTitle}>{t("screens.service.success_title")}</h2>
                                <p className={styles.SuccessText}>{t("screens.service.success_text")}</p>
                                <p className={styles.SuccessText}>
                                    {t("screens.service.success_text_contact", {
                                        notificationForwardedContactPerson:
                                            notificationForwardedContactPerson?.length > 0
                                                ? notificationForwardedContactPerson
                                                : t("screens.service.success_text_unknown_contact")
                                    })}
                                </p>
                                <UI.Button
                                    onClick={onClosePage}
                                    className={`${styles.ServiceButton} mx-auto my-4`}
                                    label={t("screens.service.close_page_button")}
                                    variant="link"
                                />
                            </>
                        ) : undefined}

                        {state === STATE.ERROR ? (
                            <>
                                <UI.Icon icon={UI.SVGIcon.Close} size={"xl"} />
                                <h2 className={styles.ErrorTitle}>{t("screens.service.error_title")}</h2>
                                <p className={styles.ErrorText}>{errorText}</p>
                                {serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000 ? (
                                    <UI.Button
                                        onClick={onSendServiceOrder}
                                        className={`${styles.ServiceButton} mx-auto my-4`}
                                        label={t("screens.service.error_button_text")}
                                    />
                                ) : (
                                    <UI.Button
                                        onClick={redirectToYarowa(state)}
                                        className={`${styles.ServiceButton} mx-auto my-4`}
                                        label={t("screens.service.redirect_to_yarowa_button")}
                                    />
                                )}
                                <UI.Button
                                    onClick={onClosePage}
                                    className={`${styles.ServiceButton} mx-auto my-4`}
                                    label={t("screens.service.close_page_button")}
                                    variant="link"
                                />
                            </>
                        ) : undefined}

                        {state === STATE.ALREADY_SUBMITTED ? (
                            <>
                                <UI.Icon icon={UI.SVGIcon.Close} size={"xl"} />
                                <h2 className={styles.AlreadySubmittedTitle}>
                                    {t(
                                        serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000
                                            ? "screens.service.already_submitted_title_service_7000"
                                            : "screens.service.already_submitted_title_yarowa"
                                    )}
                                </h2>
                                <p className={styles.AlreadySubmittedText}>
                                    {t(
                                        serviceTypeOfRequest === CONNECTION_TYPE.SERVICE7000
                                            ? "screens.service.already_submitted_text_service_7000"
                                            : "screens.service.already_submitted_text_yarowa"
                                    )}
                                </p>
                                {serviceTypeOfRequest === CONNECTION_TYPE.YAROWA ? (
                                    <UI.Button
                                        onClick={redirectToYarowa(state)}
                                        className={`${styles.ServiceButton} mx-auto my-4`}
                                        label={t("screens.service.redirect_to_yarowa_button")}
                                    />
                                ) : (
                                    <></>
                                )}
                                <UI.Button
                                    onClick={onClosePage}
                                    className={`${styles.ServiceButton} mx-auto my-4`}
                                    label={t("screens.service.close_page_button")}
                                    variant="link"
                                />
                            </>
                        ) : undefined}
                    </div>
                </UI.Col>
            </UI.Row>
            {notificationCompState.customerid && notificationCompState.id ? (
                <NotificationSubscriptionComponent
                    customerid={notificationCompState.customerid}
                    id={notificationCompState.id}
                />
            ) : undefined}
        </UI.Container>
    );
};

export const Service = Sentry.withProfiler(observer(ServiceBase));
